import { useAdminPurchaseOrders } from '../../api/admin-queries.js';
import type {
  AdminPurchaseOrder,
  AdminPurchaseOrders,
  PurchaseOrderChannelSummary,
} from '../../api/types.js';
import {
  CHANNEL_LABELS,
  DECISION_REASON_LABELS,
  formatDateTime,
  formatTimeLeft,
  PURCHASE_ORDER_STATE_LABELS,
} from '../../lib/format.js';
import { Badge } from '../badge.js';
import { EmptyState } from '../empty-state.js';
import { ErrorState } from '../error-state.js';

/** Fulfilment rate that earns the full score of a channel in the Entrega 2 rubric. */
const FULL_SCORE_RATE = 0.8;
const PERCENT = 100;

type Tone = 'neutral' | 'accent' | 'success' | 'warning';

const STATE_TONES: Readonly<Record<string, Tone>> = {
  cumplida: 'success',
  aceptada: 'accent',
  vencida: 'warning',
  rechazada: 'warning',
};

function fulfilmentRate(summary: PurchaseOrderChannelSummary): number | undefined {
  const closed = summary.received - summary.open;
  return closed > 0 ? summary.fulfilled / closed : undefined;
}

function ChannelTile({ summary }: { summary: PurchaseOrderChannelSummary }) {
  const rate = fulfilmentRate(summary);
  return (
    <li className="flex flex-col gap-2 rounded-card border border-border bg-card p-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-semibold">{CHANNEL_LABELS[summary.channel] ?? summary.channel}</h3>
        {rate !== undefined && (
          <Badge tone={rate >= FULL_SCORE_RATE ? 'success' : 'warning'}>
            {Math.round(rate * PERCENT)} % cumplidas
          </Badge>
        )}
      </div>
      <dl className="grid grid-cols-3 gap-2 text-sm">
        {[
          ['Recibidas', summary.received],
          ['Aceptadas', summary.accepted],
          ['Cumplidas', summary.fulfilled],
          ['Vencidas', summary.expired],
          ['Abiertas', summary.open],
        ].map(([label, value]) => (
          <div key={label}>
            <dt className="text-muted-foreground">{label}</dt>
            <dd className="text-lg font-semibold tabular-nums">{value}</dd>
          </div>
        ))}
        <div>
          <dt className="text-muted-foreground">Respuesta (mediana)</dt>
          <dd className="text-lg font-semibold tabular-nums">
            {summary.medianAnswerSeconds === null
              ? '—'
              : `${summary.medianAnswerSeconds.toFixed(1)} s`}
          </dd>
        </div>
      </dl>
    </li>
  );
}

function OrderRow({ order }: { order: AdminPurchaseOrder }) {
  const isOpen = order.state === 'creada' || order.state === 'aceptada';
  return (
    <tr className="border-t border-border align-top">
      <td className="px-4 py-3 whitespace-nowrap">{formatDateTime(order.firstSeenAt)}</td>
      <td className="px-4 py-3 font-mono text-xs">{order.ocId}</td>
      <td className="px-4 py-3">{CHANNEL_LABELS[order.channel] ?? order.channel}</td>
      <td className="px-4 py-3">
        {order.quantity} × {order.sku}
        <span className="block text-muted-foreground">{order.customerName}</span>
      </td>
      <td className="px-4 py-3 whitespace-nowrap tabular-nums">
        {isOpen ? formatTimeLeft(order.dueAt) : formatDateTime(order.dueAt)}
        {isOpen && order.estimatedDeliveryAt !== null && (
          <span className="block text-muted-foreground">
            estimada {formatDateTime(order.estimatedDeliveryAt)}
          </span>
        )}
      </td>
      <td className="px-4 py-3">
        {order.decision === null ? (
          <span className="text-muted-foreground">Sin decisión</span>
        ) : (
          <>
            {order.decision === 'ACCEPTED' ? 'Aceptada' : 'Rechazada'}
            {order.decisionReason !== null && (
              <span className="block text-muted-foreground">
                {DECISION_REASON_LABELS[order.decisionReason] ?? order.decisionReason}
              </span>
            )}
          </>
        )}
      </td>
      <td className="px-4 py-3">
        <Badge tone={STATE_TONES[order.state] ?? 'neutral'}>
          {PURCHASE_ORDER_STATE_LABELS[order.state] ?? order.state}
        </Badge>
      </td>
      <td className="px-4 py-3 text-right tabular-nums">
        {order.dispatched}/{order.quantity}
      </td>
    </tr>
  );
}

function AlertsBanner({ alerts }: { alerts: AdminPurchaseOrders['alerts'] }) {
  const messages = [
    alerts.unanswered.length > 0 &&
      `${String(alerts.unanswered.length)} sin responder hace más de 2 min`,
    alerts.atRisk.length > 0 &&
      `${String(alerts.atRisk.length)} aceptadas vencen en menos de 15 min sin despacho completo`,
  ].filter((message): message is string => message !== false);
  if (messages.length === 0) return null;
  return (
    <div
      role="alert"
      className="rounded-card border border-destructive/40 bg-destructive/10 px-4 py-3 text-destructive"
    >
      <p className="font-semibold">Órdenes que requieren atención</p>
      <ul className="list-disc pl-5">
        {messages.map((message) => (
          <li key={message}>{message}</li>
        ))}
      </ul>
    </div>
  );
}

const COLUMNS = [
  'Recibida',
  'OC',
  'Canal',
  'Pedido',
  'Plazo',
  'Decisión',
  'Estado',
  'Despachado',
] as const;

/** Purchase orders received by sFTP, Pub/Sub and messaging, with the per-channel rubric counters. */
export function PurchaseOrdersPanel() {
  const purchaseOrders = useAdminPurchaseOrders();
  if (purchaseOrders.isError)
    return <ErrorState message="No pudimos cargar las órdenes de compra." />;
  const data = purchaseOrders.data;
  return (
    <section aria-labelledby="purchase-orders-title" className="flex flex-col gap-4">
      <div>
        <h2 id="purchase-orders-title" className="text-xl font-bold">
          Órdenes de compra
        </h2>
        <p className="text-muted-foreground">
          Recibidas por sFTP, Pub/Sub y mensajería en las últimas 36 h.
        </p>
      </div>
      {data === undefined && <p aria-live="polite">Cargando órdenes de compra…</p>}
      {data !== undefined && <AlertsBanner alerts={data.alerts} />}
      {data !== undefined && data.channels.length > 0 && (
        <ul className="grid gap-3 md:grid-cols-3">
          {data.channels.map((summary) => (
            <ChannelTile key={summary.channel} summary={summary} />
          ))}
        </ul>
      )}
      {data?.orders.length === 0 && <EmptyState message="Todavía no llegan órdenes de compra." />}
      {data !== undefined && data.orders.length > 0 && (
        <div className="overflow-x-auto rounded-card border border-border bg-card">
          <table className="w-full min-w-[64rem] text-left text-sm">
            <thead className="bg-muted text-muted-foreground">
              <tr>
                {COLUMNS.map((column) => (
                  <th
                    key={column}
                    scope="col"
                    className={`px-4 py-3 font-medium ${column === 'Despachado' ? 'text-right' : ''}`}
                  >
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.orders.map((order) => (
                <OrderRow key={order.ocId} order={order} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
